// services/facturationService.js

import apiClient from "./api";

// Créer une facturation
export const createFacturation = async (formData) => {
  try {
    const response = await apiClient.post('/api/Facturation/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error creating facturation:", error);
    throw error("Error creating facturation:", error);
  }
};

// Générer PDF pour une facturation
export const generateFacturationPdf = async (facturationId, domainId = 1) => {
  try {
    const response = await apiClient.post(`/api/Facturation/${facturationId}/generate-pdf?domainId=${domainId}`);
    return response.data;
  } catch (error) {
    // console.error("Error generating PDF:", error);
    throw error("Error generating PDF:", error);
  }
};

// Générer tous les PDFs en attente
export const generatePendingPdfs = async (domainId = 1, trimestre = null) => {
  try {
    const params = { domainId };
    if (trimestre) params.trimestre = trimestre;
    
    const response = await apiClient.post('/api/Facturation/generate-pending-pdfs', null, { params });
    return response.data;
  } catch (error) {
    // console.error("Error generating pending PDFs:", error);
    throw error("Error generating pending PDFs:", error);
  }
};

// Vérifier statut PDF
export const getPdfStatus = async (facturationId) => {
  try {
    const response = await apiClient.get(`/api/Facturation/${facturationId}/pdf-status`);
    return response.data;
  } catch (error) {
    // console.error("Error getting PDF status:", error);
    throw error("Error getting PDF status:", error);
  }
};

// Obtenir toutes les facturations avec filtres
// service/api/facturationService.js
export const getAllFacturations = async (domainId, filters = {}) => {
  try {
    // Nettoyer les paramètres pour l'API
    const cleanFilters = { ...filters };
    
    // Convertir les dates en format ISO
    if (cleanFilters.dateDebut) {
      cleanFilters.dateDebut = new Date(cleanFilters.dateDebut).toISOString().split('T')[0];
    }
    if (cleanFilters.dateFin) {
      cleanFilters.dateFin = new Date(cleanFilters.dateFin).toISOString().split('T')[0];
    }

    // Supprimer les propriétés vides
    Object.keys(cleanFilters).forEach(key => {
      if (cleanFilters[key] === '' || cleanFilters[key] === null || cleanFilters[key] === undefined) {
        delete cleanFilters[key];
      }
    });

    const params = {
      domainId,
      ...cleanFilters
    };

    // console.log('API Params:', params); // Pour le débogage
    
    const response = await apiClient.get('/api/Facturation', { params });
    return response.data;
  } catch (error) {
    // console.error("Error fetching facturations:", error);
    throw error("Error fetching facturations:", error);
  }
};

// Supprimer une facturation
export const deleteFacturation = async (id) => {
  try {
    const response = await apiClient.delete(`/api/Facturation/delete/${id}`);
    return response.data;
  } catch (error) {
    // console.error("Error deleting facturation:", error);
    throw error("Error deleting facturation:", error);
  }
};

// services/facturationService.js - Ajouter cette fonction
// Obtenir les détails d'une facturation
export const getFacturationById = async (id) => {
  try {
    const response = await apiClient.get(`/api/Facturation/${id}`);
    return response.data;
  } catch (error) {
    // console.error("Error fetching facturation details:", error);
    throw error("Error fetching facturation details:", error);
  }
};

// === GESTION PDF BACKEND SÉCURISÉE ===

/**
 * Affiche le PDF dans le navigateur via l'API backend sécurisée
 * @param {number} facturationId - ID de la facturation
 * @returns {Promise<Object>} Résultat de l'opération
 */
export const viewFacturationPdf = async (facturationId) => {
  try {
    // Utilise l'API backend pour récupérer le PDF
    const response = await apiClient.get(`/api/Facturation/ViewFacturation/${facturationId}`, {
      responseType: 'blob', // Important pour les fichiers binaires
      timeout: 30000 // 30 secondes timeout
    });
    
    // Créer une URL blob pour l'affichage
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Ouvrir dans un nouvel onglet
    const newWindow = window.open(blobUrl, '_blank');
    
    if (!newWindow) {
      throw new Error('Popup bloqué. Veuillez autoriser les popups pour ce site.');
    }
    
    // Nettoyer la mémoire après 30 secondes
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 30000);
    
    return { 
      success: true, 
      blobUrl,
      message: 'PDF ouvert dans un nouvel onglet'
    };
  } catch (error) {
    // console.error("Error viewing PDF via backend:", error);
    
    // Gestion d'erreurs spécifiques
    if (error.response?.status === 404) {
      throw new Error('PDF non trouvé. Veuillez générer le PDF d\'abord.');
    } else if (error.response?.status === 500) {
      throw new Error('Erreur serveur lors de la récupération du PDF.');
    } else if (error.message.includes('timeout')) {
      throw new Error('Timeout lors du chargement du PDF.');
    } else {
      throw new Error(`Erreur d'affichage: ${error.message}`);
    }
  }
};

/**
 * Télécharge le PDF via l'API backend avec nom de fichier sécurisé
 * @param {number} facturationId - ID de la facturation
 * @returns {Promise<Object>} Résultat du téléchargement
 */
export const downloadFacturationPdf = async (facturationId) => {
  try {
    const response = await apiClient.get(`/api/Facturation/downloadFacturation/${facturationId}`, {
      responseType: 'blob',
      timeout: 30000
    });
    
    // Extraire le nom de fichier depuis les headers ou utiliser un nom par défaut
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `Facturation_${facturationId}_${new Date().getTime()}.pdf`;
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }
    
    // Créer le lien de téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    link.setAttribute('data-testid', 'pdf-download-link'); // Pour les tests
    
    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Nettoyer la mémoire
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 10000);
    
    return { 
      success: true, 
      fileName,
      message: `PDF téléchargé: ${fileName}`
    };
  } catch (error) {
    console.error("Error downloading PDF via backend:", error);
    
    if (error.response?.status === 404) {
      throw new Error('PDF non trouvé. Veuillez générer le PDF d\'abord.');
    } else {
      throw new Error(`Erreur de téléchargement: ${error.message}`);
    }
  }
};

/**
 * Envoie le PDF par email via l'API backend
 * @param {number} facturationId - ID de la facturation
 * @param {boolean} useSecureLink - Utiliser un lien sécurisé ou pièce jointe
 * @returns {Promise<Object>} Résultat de l'envoi
 */
export const sendFacturationByEmail = async (facturationId, useSecureLink = true) => {
  try {
    const response = await apiClient.post(
      `/api/Facturation/sendFacturation/${facturationId}?secureLink=${useSecureLink}`
    );
    
    return {
      success: true,
      data: response.data,
      message: response.data.Message || 'Email envoyé avec succès'
    };
  } catch (error) {
    // console.error("Error sending PDF by email:", error);
    
    const errorMessage = error.response?.data?.Message || 
                        error.response?.data?.message || 
                        `Erreur d'envoi: ${error.message}`;
    
    throw new Error(errorMessage);
  }
};

// services/facturationService.js - Mettre à jour les URLs
export const downloadFacturationPdfs = async (facturationId) => {
  try {
    const response = await apiClient.get(`/api/Facturation/downloadDirect/${facturationId}`, {
      responseType: 'blob',
      timeout: 30000
    });
    
    // Le reste du code reste identique...
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `Facturation_${facturationId}_${new Date().getTime()}.pdf`;
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 10000);
    
    return { 
      success: true, 
      fileName,
      message: `PDF téléchargé: ${fileName}`
    };
  } catch (error) {
    // console.error("Error downloading PDF via backend:", error);
    
    if (error.response?.status === 404) {
      throw new Error('PDF non trouvé. Veuillez générer le PDF d\'abord.');
    } else {
      throw new Error(`Erreur de téléchargement: ${error.message}`);
    }
  }
};

// Télécharger PDF
export const downloadPdf = (pdfUrl) => {
  window.open(pdfUrl, '_blank');
};

/**
 * Service pour les opérations par lot ou single sur les facturations
 */
export const payCommissionsBatch = async (facturationIds, domainId) => {
  try {
    const response = await apiClient.post('/api/Facturation/payCommissionsBatch', {
      facturationIds,
      domainId
    });
    return response.data;
  } catch (error) {
    // console.error("Error paying commissions batch:", error);
    throw error("Error paying commissions batch:", error);
  }
};

export const validateFacturationsBatch = async (facturationIds, domainId) => {
  try {
    const response = await apiClient.post('/api/Facturation/validateFacturationsBatch', {
      facturationIds,
      domainId
    });
    return response.data;
  } catch (error) {
    // console.error("Error validating facturations batch:", error);
    throw error("Error validating facturations batch:", error);
  }
};



// Opérations unitaires pour compléter
export const paySingleCommission = async (facturationId) => {
  try {
    const response = await apiClient.post(`/api/Facturation/CommisionTrimestrielle/${facturationId}`);
    return response.data;
  } catch (error) {
    // console.error("Error paying single commission:", error);
    throw error("Error paying single commission:", error);
  }
};

export const validateSingleFacturation = async (facturationId) => {
  try {
    const response = await apiClient.put(`/api/Facturation/validate/${facturationId}`);
    return response.data;
  } catch (error) {
    // console.error("Error validating single facturation:", error);
    throw error("Error validating single facturation:", error);
  }
};

// Paiement Final (Étape 3) - NOUVEAU
export const payFinalFacturation = async (facturationId) => {
  const response = await apiClient.put(`/api/Facturation/payement/${facturationId}`);
  return response.data;
};

export const payFinalFacturationsBatch = async (facturationIds, domainId) => {
  const response = await apiClient.post('/api/Facturation/PayFacturationsBatch', {
    facturationIds,
    domainId
  });
  return response.data;
};